/**
 * Shared hostname classifier — decides whether the current browser
 * host belongs to Orbit's own infrastructure (dashboard app) or to
 * a studio's custom domain (public site renderer).
 *
 * Keep this list in sync across App.tsx routing and any hook that
 * queries `custom_domains_public` by hostname.
 */

const ORBIT_EXACT_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "orbtcrm.com",
  "www.orbtcrm.com",
  "orbitstudio.com",
  "www.orbitstudio.com",
  "orbitstudiocrm.com",
  "www.orbitstudiocrm.com",
]);

// Any host ending in one of these suffixes is treated as an Orbit /
// preview host, never a studio custom domain.
const ORBIT_HOST_SUFFIXES = [
  ".lovable.app",
  ".lovable.dev",
  ".vercel.app", // Vercel preview + production deployments
  ".netlify.app",
  ".pages.dev", // Cloudflare Pages
  ".local", // mDNS / local network
  ".localhost",
];

/** Bare IPv4 like 192.168.1.10 — never a real custom domain. */
function isIpAddress(h: string): boolean {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(h) || h.startsWith("[") /* ipv6 */;
}

export function isOrbitOwnedHost(hostname: string | null | undefined): boolean {
  if (!hostname) return true;
  const h = hostname.toLowerCase();
  if (ORBIT_EXACT_HOSTS.has(h)) return true;
  if (isIpAddress(h)) return true;
  return ORBIT_HOST_SUFFIXES.some((suffix) => h.endsWith(suffix));
}

export function isCustomStudioHostname(): boolean {
  if (typeof window === "undefined") return false;
  return !isOrbitOwnedHost(window.location.hostname);
}

/**
 * SITE-BRAND-1 — the pages a studio's CLIENTS see. None of them may
 * show Orbit's own startup splash or Orbit's branded page loader:
 * a published site, a proof gallery, a portal, a signing link is the
 * studio's brand, not ours (Rennan, Sep 29: a published site opened on
 * Orbit's hyperspace intro).
 *
 * One list, read by the splash (which runs before the router mounts,
 * so it can't read the route table), the app-wide Suspense loader and
 * the cookie banner. IF YOU ADD A PUBLIC ROUTE, ADD ITS PREFIX HERE.
 * Public intake forms live at `/:orgSlug` and can't be told apart by
 * prefix; the splash handles those with its own single-segment rule.
 */
export const CLIENT_FACING_PREFIXES = [
  "/s/",
  "/site-preview",
  "/portal/",
  "/inquiry-portal/",
  "/embed/",
  "/sign/",
  "/d/",
  "/book/",
  "/delivery/",
  "/gallery/",
  "/video/",
  "/demo/",
] as const;

/** A studio's own domain, or an Orbit URL a studio sends to a client. */
export function isClientFacingSurface(pathname?: string): boolean {
  if (isCustomStudioHostname()) return true;
  const path = pathname ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  return CLIENT_FACING_PREFIXES.some((p) => path.startsWith(p));
}

/**
 * DOMAIN-4 — www.orbitstudiocrm.com is the MARKETING host.
 *
 * The split every mature product ships: `www` is the brochure
 * (always marketing, signed in or not — the link you put on a
 * business card), the bare domain is the product (signed-out
 * visitors still get marketing at `/`; signed-in studios land in
 * the app). Auth sessions live per-origin, so funneling all app
 * usage to the bare domain also means nobody ends up half-signed-in
 * on two origins.
 */
const MARKETING_WWW_HOST = "www.orbitstudiocrm.com";

/** Paths that belong to the marketing/public surface — the only
 *  routes `www` serves itself; anything else hops to the app origin. */
const MARKETING_PATHS = new Set([
  "/", "/orbit", "/about", "/terms", "/privacy", "/cookies",
]);

export function isMarketingWwwHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.toLowerCase() === MARKETING_WWW_HOST;
}

export function isMarketingPath(pathname: string): boolean {
  return MARKETING_PATHS.has(pathname.replace(/\/+$/, "") || "/");
}

/** The app origin `www` hands non-marketing traffic to. */
export const APP_ORIGIN = "https://orbitstudiocrm.com";
